import "../../../assets/css/PremiumTools.css";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../firebase.init";
import ReactMarkdown from 'react-markdown';
import toast, { Toaster } from 'react-hot-toast';
import { 
  FileText, AlignLeft, Settings, Copy, Download, Zap, 
  Edit3, Trash2, CheckCircle, RefreshCw, Maximize2, ArrowLeft 
} from 'lucide-react';

const ArticleRewriteTool = () => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const outputRef = useRef(null); // 2. Ref for the output container

  // User Data & Credits
  const [credits, setCredits] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);

  // Form State
  const [originalText, setOriginalText] = useState("");
  const [rewriteStyle, setRewriteStyle] = useState("Professional");
  const [targetAudience, setTargetAudience] = useState("");
  
  // Output State
  const [rewrittenText, setRewrittenText] = useState("");

  // Options
  const styleOptions = [
    { value: "Professional", label: "Professional & Formal" },
    { value: "Simple", label: "Simple & Clear (Grade 8)" },
    { value: "SEO", label: "SEO Optimized (Keyword Rich)" },
    { value: "Creative", label: "Creative & Engaging" },
    { value: "Academic", label: "Academic & Scholarly" },
    { value: "Persuasive", label: "Persuasive & Salesy" },
    { value: "Casual", label: "Casual & Friendly" },
  ];

  // ==========================================
  // 2. BACKEND / CREDIT LOGIC (PRESERVED)
  // ==========================================

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) setError("No API keys available.");
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          setCredits(userResponse.data[0].currentBalance);
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // ==========================================
  // 3. AI GENERATION LOGIC
  // ==========================================

  const constructPrompt = () => {
    return `You are an expert Editor and Content Rewriter.
    
    Task: Rewrite the following text.
    Target Audience: ${targetAudience || "General Reader"}
    Rewrite Style: ${rewriteStyle}
    
    Rules:
    1. Preserve the original meaning and core facts.
    2. Maintain formatting (keep headings, bullet points, and paragraph structure).
    3. Improve flow, clarity, and grammar.
    4. Use Markdown for formatting (Bold important terms, use H2/H3 for sections, use bullet points).
    5. Do not include preambles like "Here is the rewritten text". Just output the content.
    
    Input Text:
    "${originalText}"`;
  };

  const generateRewrite = async () => {
    if (!originalText.trim()) {
      toast.error("Please enter text to rewrite.");
      return;
    }
    if (originalText.length < 10) {
      toast.error("Text is too short to rewrite.");
      return;
    }
    if (credits < 1) {
      setError("Insufficient credits. You need at least 1 credit.");
      return;
    }

    setGenerating(true);
    setError("");
    setRewrittenText("");

    try {
      const deductResponse = await axios.post(
        `https://allinoneai-d1a5a1b54c64.herokuapp.com/api/deduct-credit`,
        {
          userEmail: user.email,
          amount: 1,
        }
      );

      if (!deductResponse.data.success) {
        throw new Error("Failed to deduct credits");
      }
      setCredits((prev) => prev - 1);

      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: constructPrompt() }] }],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 4096,
          },
        }),
      });

      if (!response.ok) throw new Error("AI Generation Failed");

      const data = await response.json();
      const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (generatedText) {
        setRewrittenText(generatedText);
        toast.success("Article rewritten successfully!");
      } else {
        throw new Error("Empty response from AI");
      }

    } catch (err) {
      console.error("Generation Error:", err);
      toast.error(err.message);
      setError(err.message);

      try {
        await axios.post(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: 1,
        });
        setCredits((prev) => prev + 1);
        toast("Credits refunded due to error.");
      } catch (refundErr) {
        console.error("Refund failed:", refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  // ==========================================
  // 4. UTILS (UPDATED COPY LOGIC)
  // ==========================================

  // Fixed: Copies Rich Text (HTML) for Word/Docs and Plain Text for Notepad
  const handleCopy = () => {
    if (!rewrittenText || !outputRef.current) return;

    try {
      // Get the rendered HTML (Rich Text)
      const htmlContent = outputRef.current.innerHTML;
      // Get the rendered Text (Clean Text without markdown symbols)
      const textContent = outputRef.current.innerText;

      const blobHtml = new Blob([htmlContent], { type: "text/html" });
      const blobText = new Blob([textContent], { type: "text/plain" });

      const data = [new ClipboardItem({
        "text/html": blobHtml,
        "text/plain": blobText,
      })];

      navigator.clipboard.write(data).then(() => {
        toast.success("Copied formatted text!");
      });
    } catch (err) {
      // Fallback for browsers with stricter clipboard policies
      navigator.clipboard.writeText(outputRef.current.innerText).then(() => {
        toast.success("Copied text!");
      });
    }
  };

  const downloadText = () => {
    if (!outputRef.current) return;
    // Download the Clean Text (not the Markdown source)
    const element = document.createElement("a");
    const file = new Blob([outputRef.current.innerText], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `rewritten-article-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
  };

  const getWordCount = (text) => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const handleClear = () => {
    if(window.confirm("Are you sure you want to clear the input?")) {
      setOriginalText("");
    }
  }

  // ==========================================
  // 5. RENDER
  // ==========================================

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loader-container">
        <div className="spinner"></div>
        <p>Loading Rewriter...</p>
        
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wall">
        <div className="auth-card">
          <h2>Log in Required</h2>
          <p>Please log in to use the Article Rewriter.</p>
          <button onClick={() => window.location.href = "/login"}>Go to Login</button>
        </div>
        
      </div>
    );
  }

  return (
    <div className="premium-app-container">
      {/* Background Orbs */}
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>

      {/* Main Content */}
      <div className="container py-5 position-relative z-1">
        
        {/* Header Row */}
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-light rounded-circle p-2 back-btn d-flex align-items-center justify-content-center" onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
            </button>
            <h1 className="fw-bold m-0 text-white">
              Article <span className="text-neon">Rewriter</span>
            </h1>
          </div>

          <div className="credits-badge">
            <Zap size={16} fill="#eab308" color="#eab308" />
            <span>{credits} Credits</span>
            {credits < 5 && (
              <button className="btn-buy-credits" onClick={() => window.location.href = "/user/select-package"}>
                Buy More
              </button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-danger glass-alert">{error}</div>}

        <div className="row g-4">
          {/* LEFT SIDEBAR - FORM */}
          <div className="col-lg-4">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <Edit3 size={18} className="text-neon" />
                  Settings
                </h5>
              </div>
              <div className="card-body-glass flex-grow-1 d-flex flex-column">
                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Rewrite Style</label>
                  <select 
                    className="form-select input-group-glass" 
                    value={rewriteStyle} 
                    onChange={(e) => setRewriteStyle(e.target.value)}
                  >
                    {styleOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                
                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Target Audience (Optional)</label>
                  <input 
                    type="text" 
                    className="form-control input-group-glass" 
                    value={targetAudience} 
                    onChange={(e) => setTargetAudience(e.target.value)}
                    placeholder="e.g. Beginners, Industry Experts" 
                  />
                </div>

                <div className="mb-4 flex-grow-1 d-flex flex-column">
                  <div className="d-flex justify-content-between mb-2 align-items-center">
                    <label className="form-label text-light opacity-75 m-0">Original Text</label>
                    <span className="badge bg-dark border border-secondary">{getWordCount(originalText)} words</span>
                  </div>
                  <textarea
                    className="form-control input-group-glass flex-grow-1"
                    style={{ minHeight: "250px", resize: "none" }}
                    value={originalText}
                    onChange={(e) => setOriginalText(e.target.value)}
                    placeholder="Paste your article, paragraph, or content here to rewrite..."
                  ></textarea>
                </div>

                <div className="d-flex gap-2 mt-auto">
                  <button 
                    className="btn btn-neon-primary flex-grow-1 d-flex align-items-center justify-content-center gap-2" 
                    onClick={generateRewrite}
                    disabled={generating || credits < 1 || !originalText.trim()}
                  >
                    {generating ? (
                      <><RefreshCw className="spin" size={18} /> Rewriting...</>
                    ) : (
                      <><RefreshCw size={18} /> Rewrite Article</>
                    )}
                  </button>
                  {originalText && (
                    <button className="btn btn-outline-light d-flex align-items-center justify-content-center" onClick={handleClear} title="Clear Text">
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR - OUTPUT */}
          <div className="col-lg-8">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass d-flex justify-content-between align-items-center">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <CheckCircle size={18} className={rewrittenText ? "text-success" : "text-light opacity-50"} /> 
                  Rewritten Version
                </h5>
                <div className="d-flex align-items-center gap-2">
                  {rewrittenText && (
                    <>
                      <span className="badge bg-dark border border-secondary">{getWordCount(rewrittenText)} words</span>
                      <button className="btn btn-sm btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center" onClick={handleCopy} title="Copy Formatted Text">
                        <Copy size={14} />
                      </button>
                      <button className="btn btn-sm btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center" onClick={downloadText} title="Download">
                        <Download size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              <div className="card-body-glass flex-grow-1 overflow-auto position-relative" style={{ minHeight: "500px" }}>
                {generating ? (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-75 py-5">
                    <div className="spinner-border text-neon mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="fs-5">AI is analyzing and rewriting your content...</p>
                  </div>
                ) : rewrittenText ? (
                  <div className="rich-text-content" ref={outputRef}>
                    <ReactMarkdown>{rewrittenText}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-25 py-5">
                    <FileText size={64} className="mb-3" />
                    <p className="fs-5">Rewritten content will appear here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* STYLES */}
      
    </div>
  );
};

export default ArticleRewriteTool;